import type { LayerASpec } from "@cold-case/shared/case-manifest";
import { INTERIOR_EXIT_INTERACT_TILES, TILE_SIZE } from "@cold-case/shared/constants";
import type { BuildingInteriorSpec } from "@cold-case/shared/interior-layout";

import {
  interiorExitTileCenter,
  playerNearClueMarker,
} from "./interior-exit-world.js";
import type { ZoneId } from "./map-grid.js";
import { zoneTransitTile } from "./zone-transit-world.js";

/** Queued walk-then-interact target — add variants here for new world interactables. */
export type PendingWorldInteract =
  | { kind: "npc"; instanceId: string; wx: number; wy: number }
  | { kind: "building_entry"; buildingId: string; wx: number; wy: number }
  | { kind: "zone_transit"; wx: number; wy: number }
  | { kind: "interior_exit"; wx: number; wy: number }
  | { kind: "interior_clue"; buildingId: string; wx: number; wy: number };

export type WorldInteractRangeCtx = {
  zone: ZoneId;
  layerA: LayerASpec;
  interiorSpec: BuildingInteriorSpec | null;
  transitInteractTiles: number;
  npcInteractRadiusPx: number;
  entryArrowInteractMaxDistPx: number;
  npcRigPosition: (instanceId: string) => { x: number; y: number } | null;
};

export function playerNearZoneTransit(
  px: number,
  py: number,
  zone: ZoneId,
  layerA: LayerASpec,
  transitInteractTiles: number
): boolean {
  const t = zoneTransitTile(zone, layerA);
  const tcx = t.tile_x * TILE_SIZE + TILE_SIZE / 2;
  const tcy = t.tile_y * TILE_SIZE + TILE_SIZE / 2;
  return Math.hypot(px - tcx, py - tcy) <= TILE_SIZE * transitInteractTiles;
}

export function playerNearInteriorExitTile(
  px: number,
  py: number,
  spec: BuildingInteriorSpec,
  interactTiles = INTERIOR_EXIT_INTERACT_TILES
): boolean {
  const { wx, wy } = interiorExitTileCenter(spec);
  return Math.hypot(px - wx, py - wy) <= TILE_SIZE * interactTiles;
}

/** Same radii as E-key proximity in GameScene. */
export function pendingInteractInRange(
  px: number,
  py: number,
  pending: PendingWorldInteract,
  ctx: WorldInteractRangeCtx
): boolean {
  switch (pending.kind) {
    case "npc": {
      const rig = ctx.npcRigPosition(pending.instanceId);
      if (!rig) return false;
      return Math.hypot(px - rig.x, py - rig.y) <= ctx.npcInteractRadiusPx;
    }
    case "building_entry":
      return (
        Math.hypot(px - pending.wx, py - pending.wy) <= ctx.entryArrowInteractMaxDistPx
      );
    case "zone_transit":
      return playerNearZoneTransit(
        px,
        py,
        ctx.zone,
        ctx.layerA,
        ctx.transitInteractTiles
      );
    case "interior_exit":
      return ctx.interiorSpec
        ? playerNearInteriorExitTile(px, py, ctx.interiorSpec)
        : false;
    case "interior_clue":
      return ctx.interiorSpec
        ? playerNearClueMarker(px, py, ctx.interiorSpec)
        : false;
    default:
      return false;
  }
}
